/** @format */

import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { Chain } from '@shared/data-access/interfaces';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconReactionLoveEmptyFill,
} from '@dsg-tech/homefield-angular-icons';
import { Store, UtilityService, WINDOW } from '@shared/data-access/services';
import { catchError, firstValueFrom, map, of } from 'rxjs';

import { ActivatedRoute } from '@angular/router';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { HomrDataService } from '@shared/homr/data-access';
import { IListDetails } from '@shared/favorites/data-access/interfaces';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthenticationService } from '@shared/authentication/data-access';

@Component({
  selector: 'shared-favorites-components-empty',
  templateUrl: './empty-favorites.component.html',
  styleUrls: ['./empty-favorites.component.scss'],
  imports: [HmfButtonComponent, HomefieldIconLibraryModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmptyFavoritesComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly window = inject(WINDOW) as Window;
  private readonly homrDataService = inject(HomrDataService);
  private readonly hmfIconLibraryService = inject(HomefieldIconsLibraryService);
  private readonly store = inject(Store);
  private readonly utilityService = inject(UtilityService);
  private readonly chain = inject(Chain);
  private readonly authenticationService = inject(AuthenticationService);

  isPublicList = input(false);
  list = input<IListDetails | undefined>(undefined);
  isAuthenticated = input(false);

  private readonly homrData = toSignal(
    this.homrDataService.getData$('header', 2, 'default').pipe(catchError(() => of(null))),
  );

  private getTaxonomyItem() {
    const homrData = this.homrData();

    if (!homrData || typeof homrData !== 'object' || !('content' in homrData)) {
      return undefined;
    }

    const content = (
      homrData as { content?: { Taxonomy2?: Array<{ title?: string; link?: string }> } }
    ).content;
    return content?.Taxonomy2?.[0];
  }

  readonly loading = computed(() => this.homrData() === undefined);
  readonly listPromptText = computed(() =>
    this.isPublicList() && this.visualUpliftEnabled()
      ? "The creator hasn't added any items yet."
      : 'Ready to build your list?',
  );

  readonly listSubPromptText = computed(() =>
    this.isPublicList() && this.visualUpliftEnabled()
      ? "you'll see them here once they're added."
      : 'Add some products to get started.',
  );

  readonly shopButtonText = computed(() => this.getTaxonomyItem()?.title || "What's New");

  readonly shopButtonLink = computed(
    () => this.getTaxonomyItem()?.link || '/f/shop-all-new-arrivals',
  );

  readonly visualUpliftEnabled = toSignal(
    this.store
      .selectDefined('ENVIRONMENT_KILLSWITCH_VALUES')
      .pipe(
        map((killswitch) =>
          this.utilityService.checkKillSwitch(
            'visualUpliftEnabled',
            killswitch,
            this.chain.chainIdentifierAbbr,
          ),
        ),
      ),
    { initialValue: false },
  );

  constructor() {
    this.hmfIconLibraryService.registerIcons([homefieldIconReactionLoveEmptyFill]);
  }

  getListSubheadText(): string {
    if (this.visualUpliftEnabled()) {
      return 'This List Is Currently Empty';
    } else if (this.isPublicList() && this.route.snapshot.data['isListRoute']) {
      return 'This Favorites List is Empty';
    }

    return 'This List Is Empty';
  }

  navigateToSeeWhatsNewPage() {
    this.window.location.href = this.shopButtonLink();
  }

  // redirect athletes to signin page
  async signIn(): Promise<void> {
    await firstValueFrom(
      this.authenticationService.login({
        target: this.chain.myAccountUrlAuthListSignIn,
      }),
    );
  }

  // redirect athletes to  createAccount page
  async createAccount(): Promise<void> {
    await firstValueFrom(this.authenticationService.createAccount());
  }
}
